import type { LucideIcon } from 'lucide-react';
import { LineChart, MessagesSquare, ScrollText, Scale } from 'lucide-react';

export function PlatformPreviewVisual({ icon: Icon, size = 'sm' }: { icon: LucideIcon; size?: 'sm' | 'lg' }) {
    const large = size === 'lg';

    return (
        <div
            className={`relative flex w-full items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#4c1d95] to-[#8b5cf6] ${large ? 'h-72 max-w-md' : 'h-40'}`}
        >
            <div className={`absolute -right-8 -top-8 rounded-full bg-white/10 blur-2xl ${large ? 'size-48' : 'size-32'}`} />
            <div className={`absolute -bottom-10 -left-8 rounded-full bg-white/10 blur-2xl ${large ? 'size-48' : 'size-32'}`} />
            <div
                className={`relative flex items-center justify-center rounded-2xl bg-white/15 backdrop-blur ${large ? 'size-28' : 'size-16'}`}
            >
                <Icon className={large ? 'size-14 text-white' : 'size-8 text-white'} />
            </div>
        </div>
    );
}

export interface PlatformFeatureInfo {
    slug: string;
    icon: LucideIcon;
    title: string;
    tagline: string;
    description: string;
    bullets: string[];
}

/** The cross-product capabilities that tie AIPA's three applications together — single source of truth for the header mega-menu and each feature's detail page. */
export const PLATFORM_FEATURES: PlatformFeatureInfo[] = [
    {
        slug: 'data-insights',
        icon: ScrollText,
        title: 'Data & Insights',
        tagline: 'Cross-platform reporting that turns every conversation, rate, and review into a decision.',
        description:
            'Every AIPA product generates its own reports today — but they answer the same underlying question: is this working? A conversation that ends without a lead, a rate that drifts from the market, a review that turns negative — Data & Insights is AIPA’s commitment to surfacing exactly that, product by product, without you having to piece it together yourself.',
        bullets: [
            'AI Assistant reports on conversation volume, resolution rate, and lead quality',
            'Pricing Assistant reports on rate positioning across your whole portfolio',
            'Sentiment Analysis reports on review volume, sentiment trends, and response rate',
            'Every report exportable, so it fits into whatever review process your team already runs',
        ],
    },
    {
        slug: 'analytics',
        icon: LineChart,
        title: 'Analytics',
        tagline: 'Real-time dashboards for every product — response times, conversions, and rating trends.',
        description:
            'A dashboard is only useful if it’s current. Every AIPA product ships its own real-time analytics view — the AI Assistant dashboard tracks conversations and lead conversion as they happen, the Pricing dashboard recalculates your market position as rates change, and the Sentiment dashboard updates the moment a new review comes in.',
        bullets: [
            'Live conversation and conversion metrics for your AI Assistant',
            'Rate-per-square-foot and market-position tracking, updated automatically',
            'Sentiment distribution, rating trends, and a rolling reputation score',
            'No manual refresh, no exported spreadsheet — the numbers are always current',
        ],
    },
    {
        slug: 'competitive-intelligence',
        icon: Scale,
        title: 'Competitive Intelligence',
        tagline: 'Track competitor rates and reputation side by side with your own, automatically.',
        description:
            'Knowing your own numbers is only half the picture — the real question is how you compare. AIPA tracks your competitors on both fronts at once: Pricing Assistant compares your rates against the specific competitors you choose, and Sentiment Analysis compares your review ratings and sentiment mix against those same communities, so you see both sides of your competitive position in one place.',
        bullets: [
            'Rate comparisons against the exact competitors you track, not a generic market average',
            'Review rating and sentiment-mix comparisons, refreshed on demand',
            'One matrix view showing your community next to each tracked rival',
            'Works from data you already collect — no separate research process required',
        ],
    },
    {
        slug: 'integrations',
        icon: MessagesSquare,
        title: 'Integrations',
        tagline: 'Google, Twilio, calendars, and webhooks — AIPA connects to the tools you already use.',
        description:
            'AIPA is built to sit inside the tools you already run, not replace them. Reach visitors over the channels they already use, pull in the rate and location data you already track, and let AIPA notify the systems you already rely on — all through the same set of integrations powering the platform today.',
        bullets: [
            'Google Places for community and competitor location data',
            'Twilio for SMS, voice, and WhatsApp messaging',
            'Calendar sync for appointment and tour scheduling',
            'Email delivery and outbound webhooks for your own workflows',
            'One embeddable script tag to add the AI Assistant to any website',
        ],
    },
];
